/**
 * COMPONENT: c-numberList (Tier 3 — Full Web Component)
 * Auto-advancing numbered list with synchronized progress bar.
 *
 * Cycles through list items on a timer. Each active item shows
 * a progress bar (via CSS animation on div::before) that fills
 * exactly over the speed duration. Pauses on hover/focus.
 * Clicking any item snaps to it and stops auto-advance permanently.
 *
 * Attributes:
 *   speed      — hold time per item in ms (default: 3000)
 *   transition — crossfade time between items in ms (default: 500)
 *
 * State attributes (set by JS, consumed by CSS):
 *   [paused]   — freezes progress bar animation
 *   [stopped]  — permanently stopped (click). Bar filled, no animation.
 *   [last]     — active item is the last in the list. Bar hidden.
 *
 * Accessibility:
 *   - Each <li> contains a <button> — native keyboard/focus/click semantics
 *   - List semantics preserved: screen readers announce item count
 *   - Active item announced via aria-current="step" on the <button>
 *   - Inactive descriptions hidden via aria-hidden on <p>
 *   - aria-label on each <button> sourced from the item title (set in PHP)
 *   - Auto-advance disabled for prefers-reduced-motion
 *   - Container has role="group" and aria-label in markup
 */

class NumberList extends HTMLElement {

  constructor() {
    super();
    this._items = [];     // <li> elements
    this._buttons = [];   // <button> elements (one per li)
    this._activeIndex = 0;
    this._timer = null;
    this._phase = 'idle';
    this._paused = false;
    this._stopped = false;
    this._pauseAfterTransition = false;
    this._speedStartTime = 0;
    this._speedRemaining = 0;
  }

  connectedCallback() {
    this._items = [...this.querySelectorAll('ul > li')];
    this._buttons = [...this.querySelectorAll('ul > li > button')];

    if (!this._items.length || this._buttons.length !== this._items.length) return;

    const speed = parseInt(this.getAttribute('speed')) || 3000;
    const transition = parseInt(this.getAttribute('transition')) || 500;

    this.style.setProperty('--nl-speed', speed + 'ms');
    this.style.setProperty('--nl-transition', transition + 'ms');

    // Bind click/keyboard on each <button>
    this._buttons.forEach((btn, i) => {
      btn.addEventListener('click', () => this._handleClick(i));
    });

    this._activate(0);
    this._startSpeedPhase();

    // Respect reduced motion — disable auto-advance
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      this._clearTimer();
      this._stopped = true;
      this.setAttribute('stopped', '');
    }

    this.addEventListener('mouseenter', this._handlePause);
    this.addEventListener('mouseleave', this._handleResume);
    this.addEventListener('focusin', this._handlePause);
    this.addEventListener('focusout', this._handleResume);
  }

  disconnectedCallback() {
    this._clearTimer();
    this.removeEventListener('mouseenter', this._handlePause);
    this.removeEventListener('mouseleave', this._handleResume);
    this.removeEventListener('focusin', this._handlePause);
    this.removeEventListener('focusout', this._handleResume);
  }

  get speed() {
    return parseInt(this.getAttribute('speed')) || 3000;
  }

  get transition() {
    return parseInt(this.getAttribute('transition')) || 500;
  }

  // -----------------------------------------------------------
  // ACTIVATION
  // -----------------------------------------------------------

  _activate(index) {
    this._items.forEach((item, i) => {
      const btn = this._buttons[i];
      const desc = item.querySelector('p');

      item.classList.remove('-active', '-exiting');
      btn.removeAttribute('aria-current');
      if (desc) desc.setAttribute('aria-hidden', 'true');
    });

    const item = this._items[index];
    const btn = this._buttons[index];
    const desc = item.querySelector('p');

    item.classList.add('-active');
    btn.setAttribute('aria-current', 'step');
    if (desc) desc.removeAttribute('aria-hidden');

    this._activeIndex = index;
    this.toggleAttribute('last', index === this._items.length - 1);
  }

  _nextIndex() {
    return (this._activeIndex + 1) % this._items.length;
  }

  // -----------------------------------------------------------
  // PHASE MANAGEMENT
  // -----------------------------------------------------------

  _startSpeedPhase() {
    this._phase = 'speed';
    this._speedStartTime = performance.now();
    this._speedRemaining = this.speed;

    this.removeAttribute('paused');

    this._timer = setTimeout(() => {
      this._startTransitionPhase();
    }, this._speedRemaining);
  }

  _startTransitionPhase() {
    this._phase = 'transition';

    this._items[this._activeIndex].classList.add('-exiting');

    this._timer = setTimeout(() => {
      this._activate(this._nextIndex());

      if (this._pauseAfterTransition) {
        this._pauseAfterTransition = false;
        this._phase = 'speed';
        this._speedRemaining = this.speed;
        this.setAttribute('paused', '');
        return;
      }

      this._startSpeedPhase();
    }, this.transition);
  }

  // -----------------------------------------------------------
  // CLICK — SNAP & STOP
  // -----------------------------------------------------------

  _handleClick(clickedIndex) {
    if (this._stopped && clickedIndex === this._activeIndex) return;

    this._clearTimer();
    this._stopped = true;
    this._paused = false;
    this._pauseAfterTransition = false;
    this.setAttribute('stopped', '');
    this.removeAttribute('paused');

    this._activate(clickedIndex);
  }

  // -----------------------------------------------------------
  // PAUSE / RESUME
  // -----------------------------------------------------------

  _handlePause = () => {
    if (this._paused || this._stopped) return;
    this._paused = true;

    if (this._phase === 'speed') {
      this._clearTimer();
      const elapsed = performance.now() - this._speedStartTime;
      this._speedRemaining = Math.max(0, this._speedRemaining - elapsed);
      this.setAttribute('paused', '');

    } else if (this._phase === 'transition') {
      this._pauseAfterTransition = true;
    }
  };

  _handleResume = () => {
    if (!this._paused || this._stopped) return;
    if (this.matches(':hover') || this.contains(document.activeElement)) return;

    this._paused = false;
    this._pauseAfterTransition = false;

    if (this._phase === 'speed') {
      this._speedStartTime = performance.now();
      this.removeAttribute('paused');

      this._timer = setTimeout(() => {
        this._startTransitionPhase();
      }, this._speedRemaining);
    }
  };

  // -----------------------------------------------------------
  // CLEANUP
  // -----------------------------------------------------------

  _clearTimer() {
    if (this._timer) {
      clearTimeout(this._timer);
      this._timer = null;
    }
  }
}

window.customElements.define('c-numberlist', NumberList);